import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Sparkles,
  ScanText,
  Workflow,
  Cloud,
  UserCheck,
  Boxes,
  GitCompare,
  Lightbulb,
  Puzzle,
  Layers,
  ListChecks,
  FileSearch,
  Library,
  Wrench,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: ScanText,
    title: 'WFR: supplier-specific, rule-based',
    detail:
      'WebCenter Forms Recognition uses Designer-built projects per supplier (or supplier group), example-based field localization, neural validation on extracted values, and a Verifier queue for human exception handling. Templates learn from corrections over time.',
  },
  {
    icon: Sparkles,
    title: 'OCI DU: pre-trained AI, key-value',
    detail:
      'OCI Document Understanding provides pre-trained AI models for document classification and Key-Value Extraction. Custom-trained models can be added on top. Surface is REST API and OCI-native, with Oracle Process Automation as the typical workflow tier.',
  },
  {
    icon: UserCheck,
    title: 'Verifier vs Process Automation HITL',
    detail:
      'WFR has a built-in Verifier client for human-in-the-loop exception handling. OCI DU has no built-in Verifier — human review is built in the workflow tier, typically Oracle Process Automation, with confidence thresholds driving the routing.',
  },
  {
    icon: Cloud,
    title: 'Both are Oracle — both have a role',
    detail:
      'These are not competing products in Oracle\'s portfolio. WFR sits in WebCenter on Fusion Middleware. OCI DU sits in OCI. In a forward-looking WebCenter AP architecture, both can coexist — and increasingly do.',
  },
];

const ARCHITECTURE_ROWS = [
  {
    dimension: 'Deployment model',
    wfr: 'On-prem or IaaS Fusion Middleware (WebLogic). Often co-located with WebCenter Content and Capture in the same domain.',
    du: 'OCI SaaS service. Region-scoped, REST API surface, billed per page processed.',
  },
  {
    dimension: 'Training model',
    wfr: 'Supplier-specific projects built in Designer. LEAD field localization, neural validation, learning from Verifier corrections.',
    du: 'Pre-trained Key-Value Extraction and Document Classification models out of the box. Custom training supported via labeled datasets for specific document types.',
  },
  {
    dimension: 'Human-in-the-loop',
    wfr: 'Verifier client built into the product. Exception queue, side-by-side image and extracted values, learning loop back into the project.',
    du: 'Confidence scores returned from the API. HITL is constructed in the workflow tier — Oracle Process Automation tasks, custom UIs, or downstream review queues.',
  },
  {
    dimension: 'Integration model',
    wfr: 'Native to WebCenter Capture and Content. Imports/exports via WCC commits, integrates with SOA composites and BPM for downstream routing.',
    du: 'REST API consumed from Oracle Process Automation, OIC, custom apps, or OCI Functions. Output JSON is parsed and mapped by the caller.',
  },
  {
    dimension: 'Supplier coverage',
    wfr: 'Strongest on a stable, known supplier set — once a template is built and tuned, extraction quality is high and consistent.',
    du: 'Strong on long-tail and previously-unseen suppliers because no template is required. Pre-trained models generalize across layouts.',
  },
  {
    dimension: 'Runtime cost shape',
    wfr: 'WebCenter Forms Recognition license + infrastructure (WebLogic, DB, Verifier clients). Largely fixed cost, scales modestly with volume.',
    du: 'Per-page API call to OCI DU plus the workflow tier (Process Automation, OIC). Largely consumption-based, scales linearly with volume.',
  },
  {
    dimension: 'Support lifecycle',
    wfr: 'Part of WebCenter Forms Recognition product. 12c Premier Support ends December 2026; 14c (14.1.2) carries WFR forward with Premier Support through 2030.',
    du: 'OCI service — Oracle-managed, no customer patching, evergreen via OCI release cadence.',
  },
];

const WFR_RIGHT = [
  'Supplier set is stable and the top suppliers cover the bulk of invoice volume — template ROI is real.',
  'Regulated industry where high-confidence, auditable, supplier-specific extraction logic is required (healthcare, financial services, public sector).',
  'Existing investment in a WFR template library that already extracts well — Path A+ to 14c preserves that investment.',
  'WebCenter AP workflow is mature: Capture commits, WCC, SOA/BPM routing, Verifier exception queue, posting back to EBS or Fusion.',
];

const DU_RIGHT = [
  'Long-tail supplier coverage matters — many suppliers send a small number of invoices and templates would never pay back.',
  'Semi-structured or unstructured document types beyond invoices (POs, statements, receipts, contracts) where pre-trained models cover ground WFR templates do not.',
  'Cloud-native architecture is a constraint — OCI-first, no Fusion Middleware footprint, consumption-based cost shape.',
  'Oracle Process Automation is the workflow tier, with the appetite to construct human-in-the-loop review there instead of via Verifier.',
];

const BOTH_RIGHT = [
  'WFR carries the top 80% of supplier volume on known templates with high-confidence extraction.',
  'OCI DU handles the long-tail — suppliers without templates, one-off documents, exception types where building a WFR project would never pay back.',
  'Confidence thresholds and routing logic decide which engine sees which document — and where human review picks up.',
  'WebCenter Content remains the system of record; OCI DU output flows back into the same WCC repository and the same downstream AP workflow.',
];

const SCENARIOS = [
  {
    icon: Lightbulb,
    title: 'Inheriting a WFR project, considering OCI DU',
    body:
      'A new AP or IT lead inherits a WFR implementation and is evaluating whether OCI DU is the strategic forward path. The right answer depends on supplier-set stability, current WFR extraction quality, and whether the WebCenter stack is staying on Path A+ (14c upgrade) or moving to Path B (modernization). This is a comparison conversation, not a switch decision.',
  },
  {
    icon: Puzzle,
    title: 'Adding OCI DU on top of existing WFR',
    body:
      'A WebCenter AP customer with a strong WFR template library keeps WFR for the known supplier set and adds OCI DU for the long-tail — suppliers that send too few invoices to justify a template, or document types WFR was never built for. This is the most common forward architecture we see in 2026 conversations.',
  },
  {
    icon: GitCompare,
    title: 'Replacing WFR with OCI DU entirely',
    body:
      'For customers moving off WebCenter (Path B) or building a greenfield AP capture layer on OCI, OCI DU often becomes the primary extraction engine — sometimes paired with a different workflow tier than WebCenter. The Verifier-equivalent human review is built in Oracle Process Automation or a custom queue.',
  },
  {
    icon: Boxes,
    title: 'Continuing on WFR through the 14c upgrade',
    body:
      'For customers on Path A+ (12c → 14c upgrade), WFR is included and supported through Premier Support December 2030. The existing template library carries forward. OCI DU integration becomes available as an additive option, not a replacement, inside the 14c-on-OCI architecture.',
  },
];

const PATH_FRAMING = [
  {
    label: 'Path A+ — Upgrade to WebCenter 14c',
    body:
      'WebCenter Forms Recognition is included in 14c (14.1.2) and supported through Premier Support December 2030. The Designer templates, LEAD field projects, neural validation, and Verifier queue all carry forward. OCI Document Understanding becomes an additive integration option — not a replacement — typically wired in for long-tail suppliers or non-invoice document types via REST or Process Automation.',
  },
  {
    label: 'Path B — Modernize off WebCenter',
    body:
      'When the AP automation layer is replaced as part of modernization, the extraction engine is usually the modern platform\'s native AI — which may itself sit on OCI DU, or on an independent extraction stack. The WFR template library does not carry forward directly; templates are translated into the new platform\'s extraction model. The Verifier-equivalent human review is part of the modern platform\'s exception queue.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    tier: 'Tier 1 · WFR vs OCI DU Comparison Read',
    headline: 'Fixed-fee comparison for your specific implementation',
    body:
      'Andrew reads your existing WFR projects and your candidate OCI DU use case, and delivers a structured comparison sized to your supplier set, document mix, and AP workflow. Output is a written decision document — yours to keep regardless of which delivery partner you choose.',
  },
  {
    tier: 'Tier 2 · WFR + OCI DU Architecture Design',
    headline: 'Forward architecture where both engines coexist',
    body:
      'A two-to-three-week fixed-fee engagement designing the WFR + OCI DU forward architecture for your stack. Defines which suppliers and document types route to WFR vs OCI DU, how confidence thresholds drive HITL, where Process Automation fits, and how output reconciles back to WebCenter Content and EBS or Fusion.',
  },
  {
    tier: 'Tier 3 · Strategic Advisory Retainer',
    headline: 'Founder-level guidance through your decision window',
    body:
      'Monthly retainer with Andrew, sized for the customer working through a multi-month evaluation — WFR template library health, OCI DU pilot scoping, 12c → 14c upgrade, or Path B modernization. Strategic guidance on architecture choices, integration tradeoffs, and Oracle conversations.',
  },
  {
    tier: 'Tier 4 · Forward Deployed Engineering',
    headline: 'Embedded delivery on the WFR + OCI DU work itself',
    body:
      'For customers ready to build the architecture, EZ Cloud offers Forward Deployed Engineering — Andrew working alongside your team on WFR project tuning, OCI DU custom model training, Process Automation HITL design, and the integration glue back into WebCenter Content. Founder-led, scoped to a defined outcome.',
  },
];

const FAQS = [
  {
    q: 'What is Oracle WebCenter Forms Recognition?',
    a: 'WebCenter Forms Recognition (WFR) is Oracle\'s rule-based, supplier-specific document extraction product within the WebCenter suite, built on technology Oracle acquired (Brainware). Projects are built in WFR Designer per supplier or supplier group, using example-based field localization and neural validation for confidence scoring. The Verifier client provides human-in-the-loop exception handling, and corrections feed back into the project to improve future extraction. WFR is part of Oracle Fusion Middleware 12c and 14c.',
  },
  {
    q: 'What is OCI Document Understanding?',
    a: 'OCI Document Understanding (DU) is an Oracle Cloud Infrastructure AI service that provides pre-trained models for document classification and Key-Value Extraction, with support for custom-trained models on top. It is consumed via REST API and integrates natively with Oracle Process Automation as the workflow tier. Unlike WFR, no per-supplier template is required — pre-trained models generalize across layouts — and HITL is built in the workflow tier rather than via a dedicated client.',
  },
  {
    q: 'Is OCI Document Understanding a replacement for WFR?',
    a: 'No — they solve similar problems differently and the most common forward architecture uses both. For a stable supplier set with a mature WFR template library, WFR continues to deliver high-confidence, supplier-specific extraction. For long-tail suppliers and previously-unseen layouts, OCI DU\'s pre-trained models often outperform what a WFR template could deliver. The 2026 pattern we see most is WFR for the top 80% of volume, OCI DU for the long-tail and exception types.',
  },
  {
    q: 'How does the human-in-the-loop differ between WFR and OCI DU?',
    a: 'WFR has a built-in Verifier client; OCI DU has no Verifier-equivalent and constructs HITL in the workflow tier. The WFR Verifier is an exception queue with side-by-side document image and extracted values, corrections that feed back into the project, and standard supervisor and audit workflows. For OCI DU, HITL is most commonly Oracle Process Automation tasks driven by confidence-score thresholds returned by the DU API. Custom UIs and downstream review queues are also valid patterns.',
  },
  {
    q: 'Will WFR be supported after December 2026?',
    a: 'Yes — WFR is included in WebCenter 14c (14.1.2) with Premier Support through December 2030. WebCenter 12c Premier Support ends December 2026 and Extended Support ends December 2027. The 14c release also carries Extended Support through December 2033. Path A+ (12c → 14c upgrade) preserves the WFR investment and template library forward through that window.',
  },
  {
    q: 'What does each cost model look like?',
    a: 'WFR is a largely fixed cost — perpetual or term license plus supporting Fusion Middleware infrastructure (WebLogic, database, Verifier clients) — that scales modestly with volume. OCI DU is consumption-based, billed per page processed plus the cost of the workflow tier (Process Automation, OIC, or custom), and scales linearly with volume. The right cost model depends on volume profile, growth trajectory, and how the existing WFR investment is amortized.',
  },
  {
    q: 'Can OCI DU output land back in WebCenter Content?',
    a: 'Yes — OCI DU output flows into WebCenter Content via a custom but well-established integration pattern. The DU REST API returns extracted key-value pairs and classification results as JSON. From there, Oracle Process Automation, OIC, or a custom integration layer maps the output into WebCenter Content metadata, commits the document into the WCC repository, and routes through the existing downstream WebCenter AP workflow. The pattern is additive to existing WFR commit paths.',
  },
  {
    q: 'How does EZ Cloud help customers pick between WFR and OCI DU?',
    a: 'EZ Cloud delivers a structured forward architecture — WFR-only, OCI DU-only, or both — informed by 25-year founder-level WebCenter expertise. Andrew reads the existing WFR projects directly and evaluates the candidate OCI DU use case against the actual supplier mix and document types. Engagement pricing is established per situation on a Decision Call.',
  },
];

const WfrVsOciDocumentUnderstanding = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const schemaAbout = [
    'Oracle WebCenter Forms Recognition',
    'OCI Document Understanding',
    'Oracle Document Understanding AI',
    'Intelligent Document Processing',
    'AP Invoice Extraction',
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'WebCenter Forms Recognition vs OCI Document Understanding — A Real Comparison | EZ Cloud',
    description:
      'Oracle WebCenter Forms Recognition (WFR) and OCI Document Understanding (DU) solve similar problems differently. WFR is supplier-specific rule-based extraction with Verifier human-in-the-loop. OCI DU is pre-trained AI key-value extraction. Both have a place in the modern WebCenter AP stack. EZ Cloud helps customers pick the right architecture.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/wfr-vs-oci-document-understanding',
    },
    about: schemaAbout.map((name) => ({ '@type': 'Thing', name })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="WebCenter Forms Recognition vs OCI Document Understanding — A Real Comparison | EZ Cloud"
        description="Oracle WebCenter Forms Recognition (WFR) and OCI Document Understanding (DU) solve similar problems differently. WFR is supplier-specific rule-based extraction with Verifier human-in-the-loop. OCI DU is pre-trained AI key-value extraction. Both have a place in the modern WebCenter AP stack. EZ Cloud helps customers pick the right architecture."
        canonical="https://ezcloud.ai/solutions/wfr-vs-oci-document-understanding"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WFR vs OCI Document Understanding',
            url: 'https://ezcloud.ai/solutions/wfr-vs-oci-document-understanding',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <GitCompare className="w-4 h-4" aria-hidden />
              WFR vs OCI Document Understanding · Real comparison
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              WebCenter Forms Recognition vs OCI Document Understanding
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                WFR and OCI DU solve similar problems differently. When to use each, when to use both, and how they fit into a forward-looking WebCenter AP architecture.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Both are Oracle products. Both extract data from invoices and other AP documents.
              Both have a legitimate role in the modern Oracle AP stack. This is the structured
              comparison — architecture, HITL, supplier coverage, cost shape, and how the two
              engines coexist.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('wfr-vs-oci-du-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Comparison Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              TL;DR
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              The short version, before the architecture detail
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  WebCenter Forms Recognition (WFR) is supplier-specific, rule-based extraction
                  with a built-in Verifier human-in-the-loop client.
                </strong>{' '}
                Designer-built projects per supplier, LEAD field localization, neural validation,
                and a corrections loop that improves the project over time. Strongest on a stable,
                known supplier set with mature templates.
              </p>
              <p>
                <strong className="text-gray-900">
                  OCI Document Understanding (DU) is pre-trained AI key-value extraction and
                  document classification, consumed via REST API.
                </strong>{' '}
                No per-supplier template required — pre-trained models generalize across layouts.
                Custom-trained models supported on top. HITL is built in the workflow tier,
                typically Oracle Process Automation, driven by confidence-score thresholds.
              </p>
              <p>
                <strong className="text-gray-900">
                  Both are Oracle. Both have legitimate roles.
                </strong>{' '}
                In a forward-looking WebCenter AP architecture in 2026 conversations, the
                increasingly common pattern is WFR for the known top-80% of supplier volume plus
                OCI DU for the long-tail and exception types — feeding the same WebCenter Content
                repository and the same downstream AP workflow.
              </p>
            </div>
          </div>
        </section>

        {/* Key facts grid */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The shape of each product
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four facts that frame the comparison
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Before the architecture table, the load-bearing differences in plain language.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What WFR actually does */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              The WFR side
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What WebCenter Forms Recognition actually does
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                WFR is built around the idea that AP documents from a known supplier look the same
                week after week. A project is built in WFR Designer per supplier or supplier
                group: invoice number here, PO number there, line-item table in this region.
                For each field, WFR combines the rules authored in the Designer with
                example-based recognition learned from processed and verified documents, and
                <strong className="text-gray-900"> neural validation</strong> provides
                confidence scoring on the extracted values.
              </p>
              <p>
                When extraction confidence is below threshold or required fields are missing, the
                document routes to the <strong className="text-gray-900">Verifier</strong>{' '}
                client — a purpose-built exception-handling interface with the document image
                side-by-side with extracted values, supervisor and audit workflows, and a
                corrections loop that feeds back into the project to improve future extraction.
              </p>
              <p>
                Integration into the wider WebCenter AP stack is native: documents are committed
                from WebCenter Capture, extracted by WFR, and posted to WebCenter Content with
                metadata, then routed through SOA composites or BPM to the AP coding form and
                ultimately posted to Oracle E-Business Suite Payables or Fusion Cloud ERP. The
                pattern has been deployed at scale for over a decade.
              </p>
              <p>
                For customers with a stable supplier set and a mature template library, the
                question is rarely &quot;does WFR work&quot; — it does. The question is what role
                it plays in the forward architecture, especially next to OCI Document
                Understanding. See our{' '}
                <Link
                  to="/solutions/webcenter-forms-recognition-experts"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  WebCenter Forms Recognition experts page
                </Link>{' '}
                for the deeper WFR engagement picture.
              </p>
            </div>
          </div>
        </section>

        {/* What OCI DU actually does */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              The OCI DU side
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What OCI Document Understanding actually does
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                OCI Document Understanding is an Oracle Cloud Infrastructure AI service. The
                core capabilities are{' '}
                <strong className="text-gray-900">Document Classification</strong> (what kind of
                document is this — invoice, receipt, PO, statement?) and{' '}
                <strong className="text-gray-900">Key-Value Extraction</strong> (what are the
                meaningful fields and their values?). Pre-trained models cover common document
                types out of the box. Custom-trained models can be layered on top for specific
                document types or domain-specific fields.
              </p>
              <p>
                The service is consumed via REST API — typically called from{' '}
                <strong className="text-gray-900">Oracle Process Automation</strong> as the
                workflow tier, but equally callable from Oracle Integration Cloud (OIC), OCI
                Functions, or a custom application. The API returns extracted key-value pairs,
                classification results, and per-field confidence scores in JSON.
              </p>
              <p>
                There is <strong className="text-gray-900">no built-in Verifier client</strong>.
                Human-in-the-loop review is constructed in the workflow tier: confidence-score
                thresholds decide which documents auto-process and which route to a Process
                Automation task, a custom UI, or a downstream review queue. The HITL surface is
                more flexible than Verifier — and also more work to build.
              </p>
              <p>
                Because there is no per-supplier template, OCI DU shines on{' '}
                <strong className="text-gray-900">long-tail suppliers</strong>, previously-unseen
                layouts, and document types beyond invoices. The cost model is per-page
                consumption rather than perpetual license + infrastructure, which changes the
                economics meaningfully at certain volume profiles.
              </p>
            </div>
          </div>
        </section>

        {/* Architecture comparison table */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Architecture comparison
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              WFR vs OCI DU, dimension by dimension
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Seven dimensions that matter when choosing between the two — or designing an
              architecture that uses both.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/5">
                      Dimension
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      WFR
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      OCI Document Understanding
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {ARCHITECTURE_ROWS.map((row) => (
                    <tr key={row.dimension} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                        {row.dimension}
                      </td>
                      <td className="px-5 py-4 text-gray-700">{row.wfr}</td>
                      <td className="px-5 py-4 text-gray-700">{row.du}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* When WFR is the right answer */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              When WFR is the right answer
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where WFR continues to be the strongest fit
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four conditions, any one of which on its own is a strong signal that WFR is the
              right primary engine for the AP automation layer.
            </p>
            <div className="grid sm:grid-cols-2 gap-5">
              {WFR_RIGHT.map((point, idx) => (
                <Card key={idx} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <p className="text-gray-700 leading-relaxed">{point}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* When OCI DU is the right answer */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              When OCI DU is the right answer
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where OCI Document Understanding outperforms WFR
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four conditions where the pre-trained AI model and consumption-based cost shape
              decisively beat the supplier-specific template approach.
            </p>
            <div className="grid sm:grid-cols-2 gap-5">
              {DU_RIGHT.map((point, idx) => (
                <Card key={idx} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <p className="text-gray-700 leading-relaxed">{point}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* When BOTH is the right answer */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              When the right answer is both
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The increasingly common 2026 architecture: WFR + OCI DU
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              For many WebCenter AP customers, the forward architecture uses both engines —
              each playing to its strength.
            </p>
            <div className="space-y-5">
              {BOTH_RIGHT.map((point, idx) => (
                <Card key={idx} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {idx + 1}
                    </div>
                    <p className="text-gray-700 leading-relaxed pt-2">{point}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Path A+ vs Path B framing */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How this fits into Path A+ and Path B
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              WFR and OCI DU inside the December 2026 decision frame
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The WFR-vs-OCI-DU question reads differently depending on which forward path the
              broader WebCenter stack is on.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {PATH_FRAMING.map((p) => (
                <Card key={p.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-3 mb-4">
                    <Layers className="w-5 h-5 text-accent mt-1 flex-shrink-0" aria-hidden />
                    <h3 className="text-xl font-bold text-gray-900">{p.label}</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{p.body}</p>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-14c-upgrade"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the Path A+ 14c upgrade detail
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Common scenarios */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common scenarios we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where customers are when they call us
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four scenarios that bring WFR vs OCI DU comparisons onto the table. None of them
              has the same right answer.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-2">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <p className="text-center text-gray-600 mt-10 max-w-3xl mx-auto">
              For customers in scenarios 1, 2, or 4, the WFR template library matters. The{' '}
              <Link
                to="/resources/wfr-template-analyzer"
                className="text-primary font-semibold hover:text-accent transition-colors"
              >
                WFR Template Analyzer
              </Link>{' '}
              reads your existing WFR projects and produces a structured view of the supplier
              coverage, field-level extraction quality, and template-debt picture — input to the
              forward architecture decision.
            </p>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on WFR vs OCI DU
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for the comparison and the architecture
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Standard consulting tiers for the decision, plus Forward Deployed Engineering for
              the build-out itself. The same 25-year founder-level WebCenter expertise across all
              four.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t, idx) => (
                <Card key={t.tier} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.tier}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-start gap-2">
                    {idx === 0 ? (
                      <FileSearch className="w-5 h-5 text-accent mt-1 flex-shrink-0" aria-hidden />
                    ) : idx === 1 ? (
                      <Workflow className="w-5 h-5 text-accent mt-1 flex-shrink-0" aria-hidden />
                    ) : idx === 2 ? (
                      <Library className="w-5 h-5 text-accent mt-1 flex-shrink-0" aria-hidden />
                    ) : (
                      <Wrench className="w-5 h-5 text-accent mt-1 flex-shrink-0" aria-hidden />
                    )}
                    <span>{t.headline}</span>
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call. For customers
              ready to modernize the broader WebCenter AP layer, see{' '}
              <Link
                to="/solutions/modernize-webcenter-forms-recognition"
                className="text-primary font-semibold hover:text-accent transition-colors"
              >
                modernize WebCenter Forms Recognition
              </Link>
              {' '}and{' '}
              <Link
                to="/solutions/modernize-oracle-webcenter"
                className="text-primary font-semibold hover:text-accent transition-colors"
              >
                modernize Oracle WebCenter
              </Link>
              .
            </p>
          </div>
        </section>

        {/* Cross-link to Decision Guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, including where WFR and OCI DU
              sit in each, eight diagnostic questions, and a vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              <ListChecks className="w-4 h-4 mr-2" aria-hidden />
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on WFR vs OCI Document Understanding
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through the WFR vs OCI DU decision
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist. Walk through your supplier mix, current WFR
              implementation, candidate OCI DU use case, and the forward architecture in the
              context of your specific stack.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('wfr-vs-oci-du-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Comparison Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WfrVsOciDocumentUnderstanding;
